import React from 'react';

import { Switch } from '@vlabs/uikit/components/controls/switch/Switch';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { useGetDepartment, usePatchDepartment } from '@vlabs/api/artemy/client';
import type { PermissionTag, DepartmentPermissionTag } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useAssignPermissionTagsToAllInDepartmentTranslation } from './i18n';
import type { AssignPermissionTagsForAllInDepartmentProps } from './types';

const displayName = 'AssignPermissionTagsForAllInDepartmentsSwitch';
const testid = buildTestid(displayName);

export interface AssignPermissionTagsForAllInDepartmentSwitchProps
  extends AssignPermissionTagsForAllInDepartmentProps, WithDataTestid {
  permissionTags: [PermissionTag];
}

function AssignPermissionTagsForAllInDepartmentsSwitch({
  departmentId,
  permissionTags,
  'data-testid': dataTestId = undefined,
}: AssignPermissionTagsForAllInDepartmentSwitchProps): React.JSX.Element | null {
  const { t } = useAssignPermissionTagsToAllInDepartmentTranslation();
  const [permissionTag] = permissionTags ?? [];

  const getDepartment = useGetDepartment(departmentId);
  const patchDepartment = usePatchDepartment();

  const departmentTags = getDepartment.data?.permission_tags ?? [];
  const isAssign = departmentTags.find(({ permission_tag }) => permission_tag === permissionTag)?.open;
  const isDisabled = getDepartment.isLoading || patchDepartment.isPending;

  const handleChange = (newChecked: boolean): void => {
    const data: { permission_tags: DepartmentPermissionTag[] } = {
      permission_tags: departmentTags.map((tag) => {
        if (tag.permission_tag !== permissionTag) {
          return tag;
        }

        return { ...tag, open: Boolean(newChecked) } satisfies DepartmentPermissionTag;
      }),
    };

    patchDepartment.mutate(
      { departmentId, data },
      {
        onError: () => {
          toast.error(t('toast_error'));
        },
      },
    );
  };

  if (isAssign === undefined) return null;

  return (
    <Switch
      disabled={isDisabled}
      id="assignPermissionTagsToAllInDepartment"
      label={t('label') as unknown as string}
      name="assignPermissionTagsToAllInDepartment"
      onChange={handleChange}
      value={isAssign}
      {...testid(dataTestId)}
    />
  );
}

export { AssignPermissionTagsForAllInDepartmentsSwitch };
